import React, { useEffect, useMemo, useState } from 'react';
import {
  Check,
  ClipboardList,
  Clock3,
  MapPin,
  Plane,
  Search,
  ShieldCheck,
  X,
} from 'lucide-react';
import wingitLogo from './assets/wingitlog-fixed.png';
import {
  listAdminOrganisations,
  listMembershipOrganisations,
  listMyOrganisationMemberships,
  listOrganisationMembershipRequests,
  requestOrganisationMembership,
  reviewOrganisationMembershipRequest,
} from './organisationMembershipService';
import './OrganisationMembershipDemo.css';

const STATUS_FILTERS = ['pending', 'active', 'rejected', 'all'];

const NAV_ITEMS = [
  { id: 'request', label: 'Find an organisation', icon: Search },
  { id: 'memberships', label: 'My memberships', icon: ClipboardList },
  { id: 'admin', label: 'Admin review', icon: ShieldCheck },
];

function displayStatus(status) {
  if (status === 'current') return 'Current member';
  return status === 'active' ? 'Approved' : status.charAt(0).toUpperCase() + status.slice(1);
}

function StatusBadge({ status }) {
  const Icon = status === 'active' || status === 'current'
    ? Check
    : status === 'rejected'
      ? X
      : Clock3;
  const statusClass = ['pending', 'active', 'rejected'].includes(status)
    ? status === 'active'
      ? 'approved'
      : status
    : status === 'current'
      ? 'approved'
      : 'rejected';

  return (
    <span className={`org-demo-status org-demo-status-${statusClass}`}>
      <Icon size={14} aria-hidden="true" />
      {displayStatus(status)}
    </span>
  );
}

function OrganisationMark({ organisation }) {
  const initials = String(organisation?.name || 'Org')
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

  return <span className="org-demo-mark org-demo-mark-blue" aria-hidden="true">{initials}</span>;
}

function formatRequestedAt(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
}

export default function OrganisationMembershipWorkspace() {
  const [activeScreen, setActiveScreen] = useState('request');
  const [organisations, setOrganisations] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [adminOrganisations, setAdminOrganisations] = useState([]);
  const [adminRequests, setAdminRequests] = useState([]);
  const [requestOrganisationId, setRequestOrganisationId] = useState('');
  const [selectedAdminOrganisationId, setSelectedAdminOrganisationId] = useState('');
  const [adminFilter, setAdminFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const requestOrganisation = organisations.find(
    (organisation) => organisation.organisation_id === requestOrganisationId
  );
  const selectedAdminOrganisation = adminOrganisations.find(
    (organisation) => organisation.organisation_id === selectedAdminOrganisationId
  );
  const currentMembership = memberships.find(
    (membership) => membership.organisation_id === requestOrganisationId
  );
  const pendingCount = adminRequests.filter(
    (request) => request.status === 'pending'
  ).length;

  useEffect(() => {
    let cancelled = false;

    async function loadWorkspace() {
      setLoading(true);
      setError('');

      try {
        const [
          organisationRows,
          membershipRows,
          adminOrganisationRows,
        ] = await Promise.all([
          listMembershipOrganisations(),
          listMyOrganisationMemberships(),
          listAdminOrganisations(),
        ]);

        if (cancelled) return;

        setOrganisations(organisationRows);
        setMemberships(membershipRows);
        setAdminOrganisations(adminOrganisationRows);
        setRequestOrganisationId((current) => (
          organisationRows.some((organisation) => organisation.organisation_id === current)
            ? current
            : organisationRows[0]?.organisation_id || ''
        ));
        setSelectedAdminOrganisationId((current) => (
          adminOrganisationRows.some((organisation) => organisation.organisation_id === current)
            ? current
            : adminOrganisationRows[0]?.organisation_id || ''
        ));
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError.message || 'Could not load organisation membership data.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadWorkspace();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadAdminRequests() {
      if (
        activeScreen !== 'admin'
        || !selectedAdminOrganisationId
        || adminOrganisations.length === 0
      ) {
        setAdminRequests([]);
        return;
      }

      try {
        const rows = await listOrganisationMembershipRequests(
          selectedAdminOrganisationId,
          adminFilter
        );
        if (!cancelled) {
          setAdminRequests(rows);
          setError('');
        }
      } catch (loadError) {
        if (!cancelled) {
          setAdminRequests([]);
          setError(loadError.message || 'Could not load membership requests.');
        }
      }
    }

    loadAdminRequests();
    return () => {
      cancelled = true;
    };
  }, [activeScreen, adminFilter, adminOrganisations.length, selectedAdminOrganisationId]);

  const visibleNavigation = useMemo(
    () => NAV_ITEMS.filter((item) => (
      item.id !== 'admin' || adminOrganisations.length > 0
    )),
    [adminOrganisations.length]
  );

  async function handleRequestMembership(event) {
    event.preventDefault();
    setError('');
    setNotice('');

    if (!requestOrganisationId) {
      setError('Choose an organisation before sending a request.');
      return;
    }

    setWorking(true);
    try {
      await requestOrganisationMembership(requestOrganisationId);
      const rows = await listMyOrganisationMemberships();
      setMemberships(rows);
      setNotice(`Your request to ${requestOrganisation?.name || 'the organisation'} is pending review.`);
      setActiveScreen('memberships');
    } catch (requestError) {
      setError(requestError.message || 'Could not send the membership request.');
    } finally {
      setWorking(false);
    }
  }

  async function handleReviewRequest(request, decision) {
    setError('');
    setNotice('');
    setWorking(true);

    try {
      await reviewOrganisationMembershipRequest(request.membership_id, decision);
      const rows = await listOrganisationMembershipRequests(
        selectedAdminOrganisationId,
        adminFilter
      );
      setAdminRequests(rows);
      setNotice(`${request.applicant_name} ${decision === 'approve' ? 'approved' : 'rejected'}.`);
    } catch (reviewError) {
      setError(reviewError.message || 'Could not review this membership request.');
    } finally {
      setWorking(false);
    }
  }

  function renderRequestScreen() {
    const cannotRequest = Boolean(currentMembership);

    return (
      <>
        <div className="org-demo-page-heading">
          <div>
            <p className="org-demo-eyebrow">GROW YOUR FLYING COMMUNITY</p>
            <h1>Find an organisation</h1>
            <p>Choose a flying club or school and send a membership request.</p>
          </div>
        </div>
        <div className="org-demo-request-layout">
          <section className="org-demo-panel org-demo-request-form-panel">
            <span className="org-demo-panel-icon"><Plane size={20} /></span>
            <h2>Request membership</h2>
            <p className="org-demo-muted">Your request stays pending until an authorised organisation administrator reviews it.</p>
            <form onSubmit={handleRequestMembership}>
              <label htmlFor="org-live-request-select">Choose a flying club or school</label>
              <select
                id="org-live-request-select"
                value={requestOrganisationId}
                onChange={(event) => setRequestOrganisationId(event.target.value)}
                disabled={loading || working}
              >
                <option value="">Choose an organisation</option>
                {organisations.map((organisation) => (
                  <option key={organisation.organisation_id} value={organisation.organisation_id}>
                    {organisation.name}
                  </option>
                ))}
              </select>
              {requestOrganisation && (
                <div className="org-demo-org-preview">
                  <OrganisationMark organisation={requestOrganisation} />
                  <div>
                    <strong>{requestOrganisation.name}</strong>
                    <span><MapPin size={13} />{requestOrganisation.location || 'Location not listed'}</span>
                  </div>
                  {currentMembership && <StatusBadge status={currentMembership.status} />}
                </div>
              )}
              <button
                className="org-demo-primary-button"
                type="submit"
                disabled={loading || working || !requestOrganisationId || cannotRequest}
              >
                {working
                  ? 'Sending request...'
                  : cannotRequest
                    ? currentMembership.status === 'current'
                      ? 'Current member'
                      : `${displayStatus(currentMembership.status)} request recorded`
                    : 'Send membership request'}
              </button>
            </form>
            {currentMembership?.status === 'rejected' && (
              <p className="org-demo-form-hint">The declined request remains in the membership history. Contact an organisation administrator if you need it reviewed again.</p>
            )}
          </section>
          <aside className="org-demo-side-note">
            <ShieldCheck size={20} />
            <h3>What happens next?</h3>
            <p>Requests are validated by Wingit on the server. A selected organisation is only the request destination; it does not switch your access to organisation data.</p>
            <ol>
              <li>Your request remains <strong>Pending</strong>.</li>
              <li>The organisation owner can approve or reject it.</li>
              <li>Approval does not yet change your existing Wingit organisation or grant access to its records.</li>
            </ol>
          </aside>
        </div>
      </>
    );
  }

  function renderMemberships() {
    return (
      <>
        <div className="org-demo-page-heading">
          <div>
            <p className="org-demo-eyebrow">YOUR MEMBERSHIPS</p>
            <h1>Membership requests</h1>
            <p>Track requests made by your signed-in Wingit account.</p>
          </div>
          <button
            className="org-demo-primary-button org-demo-heading-button"
            type="button"
            onClick={() => {
              setError('');
              setNotice('');
              setActiveScreen('request');
            }}
          >
            Find an organisation
          </button>
        </div>
        <section className="org-demo-panel">
          {memberships.length ? (
            <div className="org-demo-membership-list">
              {memberships.map((membership) => (
                <article className="org-demo-membership-row" key={membership.membership_id}>
                  <OrganisationMark organisation={{ name: membership.organisation_name }} />
                  <div className="org-demo-membership-org">
                    <strong>{membership.organisation_name}</strong>
                    <span>{membership.location || 'Location not listed'}</span>
                    {membership.source === 'legacy' && <span>Existing organisation</span>}
                  </div>
                  <span className="org-demo-membership-updated">
                    {formatRequestedAt(membership.requested_at)}
                  </span>
                  <StatusBadge status={membership.status} />
                </article>
              ))}
            </div>
          ) : (
            <div className="org-demo-empty">
              <ClipboardList size={25} />
              <strong>No membership requests yet</strong>
              <span>Find an organisation to send a request.</span>
            </div>
          )}
        </section>
      </>
    );
  }

  function renderAdmin() {
    const visibleRequests = adminRequests.filter((request) => (
      adminFilter === 'all' || request.status === adminFilter
    ));

    return (
      <>
        <div className="org-demo-page-heading">
          <div>
            <p className="org-demo-eyebrow">AUTHORISED ORGANISATION OWNER</p>
            <h1>Review membership requests</h1>
            <p>
              Review requests for{' '}
              <strong>{selectedAdminOrganisation?.name || 'your organisation'}</strong>.
              The server checks your authority for every request.
            </p>
          </div>
          <span className="org-demo-admin-chip"><ShieldCheck size={15} /> OWNER REVIEW</span>
        </div>
        <section className="org-demo-panel">
          <div className="org-demo-panel-heading org-demo-review-heading">
            <div>
              <h2>Membership applications</h2>
              <p>Only organisations you are authorised to administer are available.</p>
            </div>
            <label className="org-demo-admin-organisation-select">
              <span className="sr-only">Organisation to review</span>
              <select
                value={selectedAdminOrganisationId}
                onChange={(event) => setSelectedAdminOrganisationId(event.target.value)}
                disabled={working}
              >
                {adminOrganisations.map((organisation) => (
                  <option key={organisation.organisation_id} value={organisation.organisation_id}>
                    {organisation.name}
                  </option>
                ))}
              </select>
            </label>
            <div className="org-demo-filter" aria-label="Filter requests">
              {STATUS_FILTERS.map((filter) => (
                <button
                  type="button"
                  key={filter}
                  aria-pressed={adminFilter === filter}
                  onClick={() => setAdminFilter(filter)}
                >
                  {filter === 'all'
                    ? 'All'
                    : filter === 'active'
                      ? 'Approved'
                      : displayStatus(filter)}
                </button>
              ))}
            </div>
          </div>
          {visibleRequests.length ? (
            <div className="org-demo-admin-list">
              {visibleRequests.map((request) => (
                <article className="org-demo-admin-request" key={request.membership_id}>
                  <span className="org-demo-avatar" aria-hidden="true">
                    {String(request.applicant_name || '?')
                      .split(' ')
                      .map((part) => part[0])
                      .join('')
                      .toUpperCase()}
                  </span>
                  <div className="org-demo-applicant">
                    <strong>{request.applicant_name}</strong>
                    <span>{request.applicant_email}</span>
                  </div>
                  <span className="org-demo-requested">
                    Requested {formatRequestedAt(request.requested_at)}
                  </span>
                  <StatusBadge status={request.status} />
                  {request.status === 'pending' ? (
                    <div className="org-demo-actions">
                      <button
                        type="button"
                        className="org-demo-approve-button"
                        aria-label={`Approve ${request.applicant_name}`}
                        disabled={working}
                        onClick={() => handleReviewRequest(request, 'approve')}
                      >
                        <Check size={15} /> Approve
                      </button>
                      <button
                        type="button"
                        className="org-demo-reject-button"
                        aria-label={`Reject ${request.applicant_name}`}
                        disabled={working}
                        onClick={() => handleReviewRequest(request, 'reject')}
                      >
                        <X size={15} /> Reject
                      </button>
                    </div>
                  ) : <span className="org-demo-reviewed">Reviewed</span>}
                </article>
              ))}
            </div>
          ) : (
            <div className="org-demo-empty">
              <ClipboardList size={25} />
              <strong>No {adminFilter} requests</strong>
              <span>There are no matching requests for this organisation.</span>
            </div>
          )}
        </section>
        <div className="org-demo-safety-note org-demo-admin-safety">
          <ShieldCheck size={19} />
          <p>Approval adds read-only aircraft and booking information for the selected organisation. It does not change your primary organisation or grant booking changes, training-record, or role permissions.</p>
        </div>
      </>
    );
  }

  const activeItem = visibleNavigation.find((item) => item.id === activeScreen)
    || visibleNavigation[0];
  const ActiveIcon = activeItem.icon;

  return (
    <main className="org-demo-page">
      <div className="org-demo-shell">
        <header className="org-demo-topbar">
          <a className="org-demo-wordmark" href="/dashboard" aria-label="Return to Wingit dashboard">
            <img className="org-demo-logo-image" src={wingitLogo} alt="Wingit" />
            <span className="org-demo-wordmark-divider" />
            <span className="org-demo-wordmark-caption">ORGANISATIONS</span>
          </a>
          <div className="org-demo-topbar-right">
            <span className="org-demo-demo-tag"><span /> LIVE MEMBERSHIPS</span>
          </div>
        </header>

        <div className="org-demo-body">
          <aside className="org-demo-sidebar">
            <div className="org-demo-nav-label">WORKSPACE</div>
            <nav aria-label="Organisation membership navigation">
              {visibleNavigation.map(({ id, label, icon: NavIcon }) => (
                <button
                  className={`org-demo-nav-item${activeScreen === id ? ' is-active' : ''}`}
                  type="button"
                  key={id}
                  aria-current={activeScreen === id ? 'page' : undefined}
                  onClick={() => {
                    setError('');
                    setNotice('');
                    setActiveScreen(id);
                  }}
                >
                  <NavIcon size={18} />
                  <span>{label}</span>
                  {id === 'admin' && pendingCount > 0 && (
                    <span className="org-demo-nav-count">{pendingCount}</span>
                  )}
                </button>
              ))}
            </nav>
            <div className="org-demo-sidebar-note">
              <ShieldCheck size={16} />
              <span>Live membership workflow<br />Approved memberships add read-only aircraft and booking access</span>
            </div>
          </aside>

          <section className="org-demo-content">
            <div className="org-demo-mobile-title">
              <ActiveIcon size={19} />
              <span>{activeItem.label}</span>
            </div>
            {loading && <div className="org-demo-notice" role="status">Loading membership workspace...</div>}
            {error && <div className="org-demo-live-error" role="alert">{error}</div>}
            {notice && (
              <div className="org-demo-notice" role="status">
                <Check size={17} />
                <span>{notice}</span>
                <button type="button" aria-label="Dismiss notification" onClick={() => setNotice('')}>
                  <X size={16} />
                </button>
              </div>
            )}
            {!loading && activeScreen === 'request' && renderRequestScreen()}
            {!loading && activeScreen === 'memberships' && renderMemberships()}
            {!loading && activeScreen === 'admin' && adminOrganisations.length > 0 && renderAdmin()}
            {!loading && activeScreen === 'admin' && adminOrganisations.length === 0 && (
              <div className="org-demo-empty">
                <ShieldCheck size={25} />
                <strong>Administrator access required</strong>
                <span>Your account is not authorised to review membership requests.</span>
              </div>
            )}
            <footer className="org-demo-footer">
              <span>Wingit organisation memberships</span>
              <span>Live requests · Approved read-only organisation context</span>
            </footer>
          </section>
        </div>
      </div>
    </main>
  );
}
